import { isDefined } from '../../utilities/is-defined';
import { NotificationModel } from '../low-level/notification.model';
import { TextModelOptions } from '../options/text-model-options';
import { TextModel } from '../primitive/text.model';
import { CompositeModel } from './composite.model';
import { ConfigConstants } from '../../config/config-constants';
import { DateModelOptions } from '../options/date-model-options';
import { DateModel } from '../primitive/date.model';
import moment from 'moment';

export class ConfirmationComplaintsQcaCreationSetModel extends CompositeModel {
    public functionalLoc: Readonly<TextModel>;
    public equipmentNum: Readonly<TextModel>;
    public materialNum: Readonly<TextModel>;
    public serialNum: Readonly<TextModel>;
    public equiShortDesc: Readonly<TextModel>;
    public division: Readonly<TextModel>;
    public softwareVersion: Readonly<TextModel>;
    public country: Readonly<TextModel>;
    public functionalLocAffected: Readonly<TextModel>;
    public equipmentNumAffected: Readonly<TextModel>;
    public materialNumAffected: Readonly<TextModel>;
    public serialNumAffected: Readonly<TextModel>;
    public equiShortDescAffected: Readonly<TextModel>;
    public divisionAffected: Readonly<TextModel>;
    public softwareVersionAffected: Readonly<TextModel>;
    public countryAffected: Readonly<TextModel>;
    public notificationNum: Readonly<TextModel>;
    public date: Readonly<DateModel>;
    public priority: TextModel;
    public addrNum: Readonly<TextModel>;
    public name1: Readonly<TextModel>;
    public name2: TextModel;
    public phone: TextModel;
    public mobile: Readonly<TextModel>;
    public email: TextModel;

    public constructor(notification: NotificationModel) {
        super('QcaCreationSet', {});
        const mainEquipment = notification.getMainEquipment();
        if (!isDefined(mainEquipment)) {
            throw new Error(`ConfirmationComplaintsQcaCreationSetModel: No main equipment found!`);
        }
        this.functionalLoc = <TextModel>this.set(new TextModel('FunctionalLoc', notification.functionalLoc));
        this.equipmentNum = <TextModel>this.set(new TextModel('EquipmentNum', mainEquipment.equnr.value));
        this.materialNum = <TextModel>this.set(new TextModel('MaterialNum', mainEquipment.matnr.value));
        this.serialNum = <TextModel>this.set(new TextModel('SerialNum', mainEquipment.sernr.value));
        this.equiShortDesc = <TextModel>this.set(new TextModel('EquiShortDesc', mainEquipment.equiShortDesc.value));
        this.division = <TextModel>this.set(new TextModel('Division', mainEquipment.division.value));
        this.softwareVersion = <TextModel>this.set(new TextModel('SoftwareVers', mainEquipment.softwareVers.value));
        // 'Country' is a mandatory property, but unfortunately notification.address.country is not guaranteed to be
        // maintained. Therefore (as a workaround) we take the first two letters of the sales org of the notification
        // and treat it as country value if the address country value is empty.
        this.country = <TextModel>(
            this.set(
                new TextModel('Country', notification.address.country.value || notification.salesOrg.substring(0, 2)),
            )
        );
        // "affected" fields are not actually used in the interface, but need to be present on odata
        this.functionalLocAffected = <TextModel>(
            this.set(new TextModel('FunctionalLocAffected', '', new TextModelOptions({ required: true })))
        );
        this.equipmentNumAffected = <TextModel>(
            this.set(new TextModel('EquipmentNumAffected', '', new TextModelOptions({ required: true })))
        );
        this.materialNumAffected = <TextModel>(
            this.set(new TextModel('MaterialNumAffected', '', new TextModelOptions({ required: true })))
        );
        this.serialNumAffected = <TextModel>(
            this.set(new TextModel('SerialNumAffected', '', new TextModelOptions({ required: true })))
        );
        this.equiShortDescAffected = <TextModel>(
            this.set(new TextModel('EquiShortDescAffected', '', new TextModelOptions({ required: true })))
        );
        this.divisionAffected = <TextModel>(
            this.set(new TextModel('DivisionAffected', '', new TextModelOptions({ required: true })))
        );
        this.softwareVersionAffected = <TextModel>(
            this.set(new TextModel('SoftwareVersAffected', '', new TextModelOptions({ required: true })))
        );
        this.countryAffected = <TextModel>(
            this.set(new TextModel('CountryAffected', '', new TextModelOptions({ required: true })))
        );
        this.notificationNum = <TextModel>this.set(new TextModel('NotificationNo', notification.identifier.qmNum));
        this.date = <DateModel>(
            this.set(new DateModel('Date', moment().startOf('day'), new DateModelOptions({ required: true })))
        );
        this.priority = <TextModel>this.set(new TextModel('Priok', '', new TextModelOptions({ required: true })));
        this.addrNum = <TextModel>this.set(new TextModel('AddrNumber', notification.address.addrNumber.value));
        this.name1 = <TextModel>this.set(new TextModel('Name1', ''));
        this.mobile = <TextModel>this.set(new TextModel('MobileNumber', ''));
        this.updateInitiator(
            notification.getContactName(),
            notification.address.email.value,
            notification.getContactPhone(),
        );
    }

    public toJSON(): JsonConfirmationComplaintsQcaCreationSetModel {
        return {
            priority: this.priority.value,
            name2: this.name2.value,
            phone: this.phone.value,
            email: this.email.value,
        };
    }

    /**
     * Updates the initiating person details
     *
     * @param name name of initiator
     * @param email email of initiator
     * @param phone phone of initiator
     */
    public updateInitiator(name: string, email: string, phone: string): void {
        this.name2 = <TextModel>(
            this.set(new TextModel('Name2', name, new TextModelOptions({ required: true, maxLength: 40 })))
        );
        this.phone = <TextModel>(
            this.set(new TextModel('TelNumber', phone, new TextModelOptions({ required: true, maxLength: 20 })))
        );
        this.email = <TextModel>this.set(
            new TextModel(
                'Email',
                email,
                new TextModelOptions({
                    required: true,
                    maxLength: 240,
                    pattern: ConfigConstants.emailValidationRegex,
                }),
            ),
        );
    }
}

export class ConfirmationComplaintsQcaCreationSetModelFromJson extends ConfirmationComplaintsQcaCreationSetModel {
    public constructor(data: JsonConfirmationComplaintsQcaCreationSetModel, notification: NotificationModel) {
        super(notification);
        this.priority.value = data.priority ? data.priority : this.priority.value;
        if (data.name2 || data.email || data.phone) {
            this.updateInitiator(data.name2 ?? '', data.email ?? '', data.phone ?? '');
        }
    }
}

export interface JsonConfirmationComplaintsQcaCreationSetModel {
    priority: string;
    name2: string;
    phone: string;
    email: string;
}
